import withDisplayName from 'bz-utilities/hoc/display-name';
import type { Constructable } from 'bz-utilities/ts';
import React from 'react';

import { cache } from 'tm3-core/cache';

import { useInitialEffect } from '../initial-effect';
import useUnmountEffect from '../unmount-effect';
import useUpdateEffect from '../update-effect';

export function useMediator<T extends object>(
  eventType: Constructable<T>,
  callback: (event: T) => void // The callback that handles the event data
) {
  const _et = React.useRef(eventType);
  const _cb = React.useRef(callback);

  useInitialEffect(() => {
    cache.mediator.subscribe<T>(eventType, callback);
  });

  useUpdateEffect(() => {
    cache.mediator.unsubscribe<T>(_et.current, _cb.current);
    _et.current = eventType;
    _cb.current = callback;

    cache.mediator.subscribe<T>(eventType, callback);
  }, [callback, eventType]);

  useUnmountEffect(() => {
    cache.mediator.unsubscribe<T>(_et.current, _cb.current);
  });
}

export interface IProps<T> extends React.PropsWithChildren {
  eventType: Constructable<T>;
  callback: (event: T) => void;
}

type GenericFC = <T extends unknown>(
  ...args: Parameters<React.FC<IProps<T>>>
) => ReturnType<React.FC>;

export const UseMediator = React.memo(
  withDisplayName(<T extends object>(props: IProps<T>) => {
    useMediator(props.eventType, props.callback);
    return props.children;
  }, 'UseMediator')
) as GenericFC;
